<template>
  <div class="role-selector-container">
    <div class="roles-list">
      <template v-for="role in displayRoles">
        <div :key="`role-${collaborator.id}-${role.id}`" class="role-item">
          <button
            type="button"
            class="role-button"
            :class="getRoleButtonClass(role)"
            :disabled="disabled || !role.is_editable || isRoleLockedForInheritedUser(role)"
            @click.stop.prevent="toggleRole(role)"
            :title="getRoleTooltip(role)"
          >
            <span class="role-indicator"></span>
            <CIcon v-if="role.scope" :name="getScopeIcon(role.scope)" size="sm" class="mr-1" />
            <span class="role-name">{{ role.name || '' }}</span>
            <span v-if="role.expires_at" class="role-expiry ml-2 text-muted small">
              <CIcon name="cilClock" size="sm"/>
              {{ formatExpiry(role.expires_at) }}
            </span>

            <button
              v-if="!disabled"
              type="button"
              class="role-info-btn"
              @click.stop.prevent="$emit('view-permissions', role)"
              :title="$gettext('View role permissions')"
              tabindex="-1"
            >
              <CIcon name="cilInfo" size="sm"/>
            </button>
          </button>

          <small class="text-muted d-block ml-3 mt-1">
            <span v-if="role.scope">{{ formatScope(role.scope) }}</span>
            <span v-if="role.is_inherited && role.scope"> • </span>
            <span v-if="role.is_inherited" v-translate translate-context="collaborators.role">Inherited</span>
          </small>
        </div>
      </template>
    </div>

    <button
      v-if="!disabled && hasMoreRoles"
      type="button"
      class="more-roles-button btn btn-sm btn-link p-1"
      @click.stop="$emit('open-role-modal')"
      v-c-tooltip="{
        content: `${additionalRolesCount} ${$gettext('more roles available')}`,
        placement: 'top'
      }"
    >
      <CIcon name="cilOptions" size="lg"/>
    </button>
  </div>
</template>

<script>
import {sortRolesByPriority} from '../utils/roles'

export default {
  name: 'RoleSelector',
  
  props: {
    collaborator: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    },
    pendingChanges: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isInheritedUser: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    displayRoles() {
      // Ordina i ruoli per rilevanza e mostra tutti i ruoli
      const roles = this.getSortedRolesByRelevance()
      return roles
    },

    hasMoreRoles() {
      // Mostra sempre il pulsante per aprire la modale (gestione più comoda)
      return true
    },

    additionalRolesCount() {
      const totalRoles = this.getSortedRolesByRelevance()
      return totalRoles.length
    }
  },
  
  methods: {
    formatScope(scope) {
      switch(scope) {
        case 'GLOBAL':
          return 'Global'
        case 'CONTRACT_MANAGER':
          return 'Contract Manager'
        case 'PICA_SERVICE':
          return 'Pica Service'
        case 'DJANGO':
          return 'Django'
        default:
          return scope
      }
    },
    
    getScopeIcon(scope) {
      switch(scope) {
        case 'GLOBAL':
          return 'cilGlobeAlt'
        case 'CONTRACT_MANAGER':
          return 'cilBriefcase'
        case 'PICA_SERVICE':
          return 'cilHome'
        case 'DJANGO':
          return 'cilSettings'
        default:
          return 'cilBadge'
      }
    },
    
    getSortedRolesByRelevance() {
      const currentRoles = Array.isArray(this.collaborator.roles) ? this.collaborator.roles : []
      const availableRoles = Array.isArray(this.availableRoles) ? this.availableRoles : []
      const roleMap = new Map()
      
      // Aggiungi i ruoli attualmente assegnati
      currentRoles.forEach(role => {
        // Trova il ruolo corrispondente nei ruoli disponibili per info aggiuntive
        const availableRole = availableRoles.find(r => r.id === role.id)
        const roleId = parseInt(role.id)
        
        // Per utenti ereditati, i ruoli già assegnati non sono editabili
        const isEditable = this.isInheritedUser ? false : (role.is_editable !== undefined ? role.is_editable : true)
        
        roleMap.set(role.id, {
          ...role,
          // is_editable determinato in base allo stato di ereditarietà
          is_editable: isEditable,
          // Se il ruolo è anche nella lista disponibili, prendi le info da lì
          is_highlighted: availableRole ? availableRole.is_highlighted : role.is_highlighted,
          is_contract_manager: availableRole ? availableRole.is_contract_manager : role.is_contract_manager,
          isActive: true,
          isPending: this.pendingChanges && this.pendingChanges[roleId] === false
        })
      })
      
      // Aggiungi i ruoli disponibili con modifiche pendenti
      availableRoles.forEach(role => {
        if (!roleMap.has(role.id)) {
          const roleId = parseInt(role.id)
          const isPending = this.pendingChanges && this.pendingChanges[roleId] === true
          if (isPending) {
            roleMap.set(role.id, {
              ...role,
              is_editable: true, // I ruoli dalla lista disponibili sono sempre editabili
              isActive: false,
              isPending: true
            })
          }
        }
      })
      
      // Aggiungi altri ruoli disponibili
      availableRoles.forEach(role => {
        if (!roleMap.has(role.id)) {
          roleMap.set(role.id, {
            ...role,
            is_editable: true, // I ruoli dalla lista disponibili sono sempre editabili
            isActive: false,
            isPending: false
          })
        }
      })
      
      // Usa la utility per ordinare i ruoli
      const allRoles = Array.from(roleMap.values())
      const assignedRoleIds = allRoles.filter(r => r.isActive).map(r => r.id)
      return sortRolesByPriority(allRoles, assignedRoleIds)
    },
    
    getRoleButtonClass(role) {
      const classes = []
      
      // Determina se il ruolo è attualmente selezionato
      const roleId = parseInt(role.id)
      let isSelected
      if (this.pendingChanges && this.pendingChanges[roleId] !== undefined) {
        // Se c'è una modifica pendente, usa quella
        isSelected = this.pendingChanges[roleId]
      } else {
        // Altrimenti usa lo stato originale
        isSelected = role.isActive
      }
      
      if (isSelected) {
        classes.push('role-active')
      }
      
      if (!role.is_editable || this.disabled) {
        classes.push('role-disabled')
      }
      
      // Mostra pending se c'è una modifica pendente
      if (this.pendingChanges && this.pendingChanges[roleId] !== undefined) {
        classes.push('role-pending')
      }
      
      return classes.join(' ')
    },
    
    toggleRole(role) {
      if (this.disabled || !role.is_editable) return
      
      // Determina lo stato attuale del ruolo considerando le modifiche pendenti
      const roleId = parseInt(role.id)
      let currentState
      if (this.pendingChanges && this.pendingChanges[roleId] !== undefined) {
        // Se c'è una modifica pendente, usa quella
        currentState = this.pendingChanges[roleId]
      } else {
        // Altrimenti usa lo stato originale
        currentState = role.isActive
      }
      
      // Per utenti ereditati, non permettere la rimozione di ruoli già assegnati
      if (this.isInheritedUser && role.isActive && currentState) {
        // Se il ruolo è già attivo e l'utente è ereditato, non permettere la rimozione
        return
      }
      
      this.$emit('toggle', this.collaborator.id, role.id, !currentState)
    },
    
    isRoleLockedForInheritedUser(role) {
      // Un ruolo è bloccato se l'utente è ereditato e il ruolo era già assegnato originariamente
      if (!this.isInheritedUser) return false
      
      // Se il ruolo era attivo dall'inizio (non aggiunto tramite pending changes)
      const currentRoles = Array.isArray(this.collaborator.roles) ? this.collaborator.roles : []
      const wasOriginallyAssigned = currentRoles.some(r => r.id === role.id)
      
      return wasOriginallyAssigned
    },
    
    getRoleTooltip(role) {
      if (this.isRoleLockedForInheritedUser(role)) {
        return this.$gettext('This role cannot be removed from inherited users')
      }
      if (!role.is_editable) {
        return this.$gettext('This role is read-only')
      }
      return ''
    },
    
    formatExpiry(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const daysLeft = Math.ceil((date - now) / (1000 * 60 * 60 * 24))
      
      if (daysLeft < 0) {
        return this.$gettext('Expired')
      } else if (daysLeft === 0) {
        return this.$gettext('Expires today')
      } else if (daysLeft === 1) {
        return this.$gettext('Expires tomorrow')
      } else if (daysLeft <= 30) {
        return this.$ngettext('%{n} day left', '%{n} days left', daysLeft, {n: daysLeft})
      } else {
        return date.toLocaleDateString()
      }
    }
  }
}
</script>

<style scoped>
.role-selector-container {
  display: flex;
  align-items: center;
  position: relative;
  gap: 8px;
  max-width: 100%;
}

.roles-list {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  flex: 1;
  align-items: center;
  gap: 12px;
  padding-bottom: 2px;
  scrollbar-width: none; /* Firefox */
  max-width: calc(100% - 40px);
}

/* Nascondi scrollbar ma mantieni funzionalità scroll */
.roles-list::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Aggiungi un indicatore visivo per lo scroll - fade sul bordo destro */
.role-selector-container::after {
  content: '';
  position: absolute;
  right: 40px;
  top: 0;
  bottom: 0;
  width: 30px;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.9));
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.role-selector-container:hover::after {
  opacity: 1;
}

.role-item {
  display: inline-block;
  flex-shrink: 0;
}

.more-roles-button {
  position: sticky;
  right: 0;
  flex-shrink: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  margin-left: 4px;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
}

.more-roles-button:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.role-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
  white-space: nowrap;
  position: relative;
}

.role-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.role-info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 2px 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 3px;
}

.role-info-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.role-info-btn:focus {
  outline: none;
}

.role-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.role-indicator {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: white;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.role-name {
  font-weight: 500;
  color: #374151;
}

.role-expiry {
  font-size: 12px;
}

.role-button.role-active .role-indicator {
  background: #10b981;
  border-color: #10b981;
}

.role-button.role-active .role-indicator::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 10px;
  font-weight: bold;
}

.role-button.role-disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #f9fafb;
}

.role-button.role-disabled .role-name {
  color: #6b7280;
}

.role-button.role-disabled.role-active .role-indicator {
  background: #6b7280;
  border-color: #6b7280;
}

.role-button.role-pending {
  border-color: #f59e0b;
  background: #fffbeb;
}

.role-button.role-pending .role-indicator {
  border-color: #f59e0b;
}

.role-button.role-pending.role-active .role-indicator {
  background: #f59e0b;
  border-color: #f59e0b;
}
</style>